import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery,
  Fab
} from '@mui/material';
import {
  PhotoCamera,
  CameraAlt,
  Delete,
  Upload,
  Close,
  FlipCameraIos,
  Refresh,
  Cameraswitch
} from '@mui/icons-material';
import { getCameraWithClosestFocus, correctImageOrientation } from '../utils/cameraUtils';

interface SensorPhotoCaptureProps {
  currentPhoto?: string | null;
  onPhotoUpdate: (photoData: string) => void;
  onPhotoDelete: () => void;
  loading?: boolean;
  disabled?: boolean;
}

const SensorPhotoCapture: React.FC<SensorPhotoCaptureProps> = ({
  currentPhoto,
  onPhotoUpdate,
  onPhotoDelete,
  loading = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get available cameras
  const getAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(cameras);
    } catch (err) {
      console.error('Error getting cameras:', err);
    }
  }, []);

  // Start camera stream
  const startCamera = useCallback(async (deviceId?: string) => {
    try {
      setError(null);
      
      // Stop existing stream first
      if (stream) {
        console.log('Stopping existing stream...');
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }

      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.load();
      }

      // Wait for cleanup
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get best camera if no specific device requested
      const selectedDeviceId = deviceId || await getCameraWithClosestFocus();
      console.log('Starting camera with deviceId:', selectedDeviceId);
      
      // Try progressively simpler constraints
      let newStream: MediaStream | undefined;
      const constraintOptions = [
        // Option 1: Basic constraints with device ID
        {
          video: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true
        },
        // Option 2: Just video true
        {
          video: true
        }
      ];

      console.log('Trying progressive constraints...');
      let constraintIndex = 0;
      
      while (constraintIndex < constraintOptions.length) {
        try {
          const currentConstraints = constraintOptions[constraintIndex];
          console.log(`Attempt ${constraintIndex + 1} with constraints:`, currentConstraints);
          newStream = await navigator.mediaDevices.getUserMedia(currentConstraints);
          console.log('Camera stream obtained successfully:', newStream);
          break;
        } catch (err) {
          console.warn(`Constraint attempt ${constraintIndex + 1} failed:`, err);
          constraintIndex++;
          if (constraintIndex >= constraintOptions.length) {
            throw err;
          }
        }
      }
      
      if (!newStream) {
        throw new Error('Failed to obtain camera stream');
      }
      
      setStream(newStream);
      setCurrentDeviceId(selectedDeviceId || null);

      if (videoRef.current) {
        const video = videoRef.current;
        
        console.log('Configuring video element...');
        
        // Complete reset of video element
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
        
        // Wait a bit for the reset to take effect
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Multiple approaches to start video
        const tryPlay = async () => {
          try {
            console.log('Attempting to play video...');
            const playPromise = video.play();
            if (playPromise !== undefined) {
              await playPromise;
              console.log('Video playing successfully');
            }
          } catch (err) {
            console.error('Error playing video:', err);
          }
        };
        
        // Set all properties before setting srcObject
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.defaultMuted = true;
        video.controls = false;
        
        // Event listeners
        video.onloadstart = () => console.log('Video onLoadStart event');
        video.onloadeddata = () => console.log('Video onLoadedData event');
        video.onloadedmetadata = () => {
          console.log('Video onLoadedMetadata event - dimensions:', video.videoWidth, 'x', video.videoHeight);
          console.log('Video readyState:', video.readyState);
          tryPlay();
        };
        video.oncanplay = () => {
          console.log('Video onCanPlay event - readyState:', video.readyState);
          tryPlay();
        };
        video.oncanplaythrough = () => {
          console.log('Video onCanPlayThrough event');
          tryPlay();
        };
        video.onplaying = () => {
          console.log('Video onPlaying event - SUCCESS!');
          console.log('Final dimensions:', video.videoWidth, 'x', video.videoHeight);
        };
        video.onpause = () => console.log('Video onPause event');
        video.onwaiting = () => console.log('Video onWaiting event');
        video.onstalled = () => console.log('Video onStalled event');
        video.onsuspend = () => console.log('Video onSuspend event');
        video.onerror = (e) => {
          console.error('Video onError event:', e);
          console.error('Video error details:', video.error);
          setError('Erreur de lecture vidéo');
        };
        
        console.log('Setting srcObject...');
        video.srcObject = newStream;
        
        console.log('Video element configured, waiting for events...');
        
        // Force play attempts with increasing delays
        setTimeout(tryPlay, 200);
        setTimeout(tryPlay, 500);
        setTimeout(tryPlay, 1000);
        setTimeout(tryPlay, 2000);
        setTimeout(tryPlay, 3000);
      }
    } catch (err: any) {
      console.error('Error starting camera:', err);
      setError(t('sensor.photo.cameraError') || 'Error accessing camera');
    }
  }, [stream, t]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Switch camera
  const switchCamera = useCallback(async () => {
    if (availableCameras.length <= 1) return;
    
    try {
      // Stop current stream first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      
      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Find next camera
      const currentIndex = availableCameras.findIndex(camera => camera.deviceId === currentDeviceId);
      const nextIndex = (currentIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];
      
      console.log('Switching from camera:', currentDeviceId, 'to:', nextCamera.deviceId, nextCamera.label);
      
      // Start new camera
      await startCamera(nextCamera.deviceId);
    } catch (error) {
      console.error('Error switching camera:', error);
      setError(t('sensor.photo.cameraError') || 'Error switching camera');
    }
  }, [availableCameras, currentDeviceId, startCamera, stream, t]);

  // Capture photo from video stream
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    setCapturing(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (!context) {
        console.error('Cannot get canvas context');
        setCapturing(false);
        return;
      }

      // Ensure video is playing and has dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error('Video has no dimensions:', video.videoWidth, 'x', video.videoHeight);
        setError(t('sensor.photo.cameraError') || 'Camera not ready');
        setCapturing(false);
        return;
      }

      console.log('Capturing photo from video:', video.videoWidth, 'x', video.videoHeight);
      console.log('Current camera device:', currentDeviceId);
      
      // Check if this is a back camera (higher resolution, might need special handling)
      const isBackCamera = availableCameras.find(cam => cam.deviceId === currentDeviceId)?.label.toLowerCase().includes('arrière') ||
                           availableCameras.find(cam => cam.deviceId === currentDeviceId)?.label.toLowerCase().includes('back');
      
      console.log('Is back camera:', isBackCamera);

      // For back camera on mobile, limit resolution to prevent memory issues
      let canvasWidth = video.videoWidth;
      let canvasHeight = video.videoHeight;
      
      if (isBackCamera && isMobile) {
        const maxDimension = 1024; // Limit back camera resolution on mobile
        if (canvasWidth > maxDimension || canvasHeight > maxDimension) {
          const ratio = Math.min(maxDimension / canvasWidth, maxDimension / canvasHeight);
          canvasWidth = Math.floor(canvasWidth * ratio);
          canvasHeight = Math.floor(canvasHeight * ratio);
          console.log('Limiting back camera resolution to:', canvasWidth, 'x', canvasHeight);
        }
      }

      // Set canvas size
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Clear canvas first
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Draw video frame to canvas with potential scaling
      context.drawImage(video, 0, 0, canvasWidth, canvasHeight);

      // Convert to base64 with different strategies for back vs front camera
      let dataUrl: string;
      try {
        if (isBackCamera && isMobile) {
          // For back camera on mobile, use lower quality to reduce size
          console.log('Using back camera mobile settings');
          dataUrl = canvas.toDataURL('image/jpeg', 0.7); // Lower quality for back camera
        } else {
          // Standard quality for front camera
          dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        }
        
        // Validate the data URL
        if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
          console.warn('JPEG capture failed, trying PNG');
          dataUrl = canvas.toDataURL('image/png');
        }
        
        if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
          throw new Error('Canvas toDataURL returned invalid data');
        }
        
        console.log('Photo captured successfully, size:', dataUrl.length, 'bytes');
        
        // Additional validation for back camera
        if (isBackCamera && dataUrl.length > 5 * 1024 * 1024) { // 5MB limit for back camera
          console.warn('Back camera photo too large, reducing quality');
          dataUrl = canvas.toDataURL('image/jpeg', 0.5);
        }
        
      } catch (canvasError) {
        console.error('Canvas toDataURL error:', canvasError);
        setError(t('sensor.photo.cameraError') || 'Error capturing photo');
        setCapturing(false);
        return;
      }
      
      // Update photo
      onPhotoUpdate(dataUrl);
      
      // Close camera
      setCameraOpen(false);
      stopCamera();
      setError(null);
      
    } catch (error) {
      console.error('Error capturing photo:', error);
      setError(t('sensor.photo.cameraError') || 'Error capturing photo');
    } finally {
      setCapturing(false);
    }
  }, [onPhotoUpdate, t, currentDeviceId, availableCameras, isMobile, stopCamera]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('sensor.photo.invalidFileType') || 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('sensor.photo.fileTooLarge') || 'File size must be less than 5MB');
      return;
    }

    try {
      setError(null);
      
      // Read image as data URL
      const reader = new FileReader();
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        
        try {
          // Create image to analyze dimensions
          const img = new Image();
          img.onload = async () => {
            const { width, height } = img;
            const aspectRatio = width / height;
            
            // Check if it's a portrait photo that needs correction
            if (aspectRatio < 0.9) {
              try {
                // Apply orientation 2 (horizontal flip) for portrait photos
                const correctedImage = await correctImageOrientation(result, 2);
                onPhotoUpdate(correctedImage);
              } catch (correctionError) {
                console.error('Error correcting orientation:', correctionError);
                onPhotoUpdate(result);
              }
            } else {
              // Keep original image for landscape photos
              onPhotoUpdate(result);
            }
          };
          
          img.src = result;
        } catch (orientationError) {
          console.error('Error processing orientation:', orientationError);
          onPhotoUpdate(result);
        }
      };
      
      reader.onerror = () => {
        setError(t('sensor.photo.fileReadError') || 'Error reading file');
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      setError(t('sensor.photo.fileProcessError') || 'Error processing file');
    }

    // Reset file input
    event.target.value = '';
  }, [onPhotoUpdate, t]);

  // Open camera dialog
  const handleOpenCamera = useCallback(async () => {
    setCameraOpen(true);
    setError(null);
    await getAvailableCameras();
    await startCamera();
  }, [getAvailableCameras, startCamera]);

  // Close camera dialog
  const handleCloseCamera = useCallback(() => {
    setCameraOpen(false);
    stopCamera();
    setError(null);
  }, [stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <Box>
      {/* Current Photo Display */}
      {currentPhoto && (
        <Card sx={{ maxWidth: 300, mb: 2 }}>
          <CardMedia
            component="img"
            height="200"
            image={currentPhoto}
            alt={t('sensor.photo.currentPhoto') as string}
            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
          />
          <CardActions>
            <Button
              size="small"
              color="error"
              startIcon={<Delete />}
              onClick={onPhotoDelete}
              disabled={loading || disabled}
            >
              {t('common.delete')}
            </Button>
          </CardActions>
        </Card>
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<CameraAlt />}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || disabled}
        >
          {currentPhoto
            ? (isMobile ? t('sensor.photo.retakePhoto') : t('sensor.photo.uploadPhoto'))
            : (isMobile ? t('sensor.photo.takePhoto') : t('sensor.photo.uploadPhoto'))}
        </Button>
      </Stack>


      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment" // Prefer back camera on mobile
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={handleCloseCamera}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('sensor.photo.takePhoto')}</Typography>
            <IconButton onClick={handleCloseCamera}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ position: 'relative', width: '100%', minHeight: '400px' }}>
            {/* Video Stream */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              controls={false}
              webkit-playsinline="true"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '400px',
                backgroundColor: '#000',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
              onCanPlay={() => {
                console.log('Video onCanPlay event');
                if (videoRef.current) {
                  videoRef.current.play().catch(err => {
                    console.error('Error in onCanPlay:', err);
                  });
                }
              }}
              onLoadStart={() => console.log('Video onLoadStart event')}
              onLoadedData={() => console.log('Video onLoadedData event')}
              onLoadedMetadata={() => {
                console.log('Video onLoadedMetadata event');
                if (videoRef.current) {
                  console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
                }
              }}
              onPlaying={() => console.log('Video onPlaying event - SUCCESS!')}
              onPause={() => console.log('Video onPause event')}
              onWaiting={() => console.log('Video onWaiting event')}
              onError={(e) => {
                console.error('Video onError event:', e);
                setError('Erreur de lecture vidéo');
              }}
            />

            {/* Camera Controls */}
            {stream && (
              <>
                {/* Desktop Controls (top right) */}
                {!isMobile && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      display: 'flex',
                      gap: 1
                    }}
                  >
                    {availableCameras.length > 1 && (
                      <Tooltip title={t('sensor.photo.switchCamera')}>
                        <IconButton
                          onClick={switchCamera}
                          sx={{
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                          }}
                        >
                          <FlipCameraIos />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title={t('sensor.photo.retryCamera')}>
                      <IconButton
                        onClick={() => startCamera(currentDeviceId || undefined)}
                        sx={{
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                        }}
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Mobile Controls (floating button) */}
                {isMobile && availableCameras.length > 1 && (
                  <Tooltip title={t('sensor.photo.switchCameraTooltip')}>
                    <Fab
                      color="primary"
                      size="medium"
                      onClick={switchCamera}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: 'rgba(25, 118, 210, 0.9)',
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 1)' },
                        zIndex: 1
                      }}
                    >
                      <Cameraswitch />
                    </Fab>
                  </Tooltip>
                )}
              </>
            )}

            {/* Debug info */}
            {stream && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: 1,
                  borderRadius: 1,
                  fontSize: '0.8rem'
                }}
              >
                Stream: {stream.active ? 'Active' : 'Inactive'} |
                Tracks: {stream.getVideoTracks().length} |
                Video: {videoRef.current?.videoWidth || 0}x{videoRef.current?.videoHeight || 0} |
                Ready: {videoRef.current?.readyState || 0}
              </Box>
            )}

            {/* Loading indicator */}
            {!stream && !error && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}
              >
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('sensor.photo.startingCamera')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseCamera}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={capturing ? <CircularProgress size={20} /> : <PhotoCamera />}
            onClick={capturePhoto}
            disabled={!stream || capturing}
          >
            {capturing ? t('sensor.photo.capturing') : t('sensor.photo.capture')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SensorPhotoCapture;